import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { AppContext } from "../AppProvider";
import { BackgroundBeams } from "../Components/ui/background-beams";
import CBSGCharLoader from "../Page/CBSGCharLoader";

const Career = () => {
    const [careers, setCareers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { uri } = useContext(AppContext);

    useEffect(() => {
        const fetchCareers = async () => {
            try {
                const response = await axios.get(`${uri}career-opportunities/`);
                setCareers(response.data);
                setLoading(false);
            } catch (err) {
                setError(err.message);
                setLoading(false);
            }
        };

        fetchCareers();
    }, [uri]);

    if (loading) {
        return <CBSGCharLoader />;
    }

    if (error) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <p className="text-red-500">Error: {error}</p>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>Career Opportunities at CBSG | Join Our Team</title>
                <meta name="description" content="Explore exciting career opportunities at CBSG. Join our team of experts in research, evaluation, and institutional capacity building. Make a difference in your career." />
                <meta name="keywords" content="careers, jobs, employment, CBSG, capacity building, research, evaluation, career opportunities, job openings, professional development" />
                
                {/* Open Graph Meta Tags */}
                <meta property="og:title" content="Career Opportunities at CBSG" />
                <meta property="og:description" content="Join CBSG's team of experts in research, evaluation, and institutional capacity building. Explore exciting career opportunities and make a difference." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={window.location.href} />
                <meta property="og:image" content={careers[0]?.photo || "https://cbsg-bd.org/default-career-image.jpg"} />
                
                {/* Twitter Card Meta Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Career Opportunities at CBSG" />
                <meta name="twitter:description" content="Join CBSG's team of experts in research, evaluation, and institutional capacity building." />
                <meta name="twitter:image" content={careers[0]?.photo || "https://cbsg-bd.org/default-career-image.jpg"} />
                
                {/* Additional Meta Tags */}
                <meta name="robots" content="index, follow" />
                <meta name="author" content="CBSG Team" />
                <link rel="canonical" href={window.location.href} />
                
                {/* Structured Data for Job Postings */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "ItemList",
                        "itemListElement": careers.map((career, index) => ({
                            "@type": "ListItem",
                            "position": index + 1,
                            "item": {
                                "@type": "JobPosting",
                                "title": career.title,
                                "description": career.description,
                                "datePosted": career.deadline,
                                "hiringOrganization": {
                                    "@type": "Organization",
                                    "name": career.company_name
                                },
                                "jobLocation": {
                                    "@type": "Place",
                                    "address": {
                                        "@type": "PostalAddress",
                                        "addressLocality": career.location
                                    }
                                }
                            }
                        }))
                    })}
                </script>
            </Helmet>

            <div className="min-h-screen bg-gradient-to-b from-white to-gray-100">
                {/* Header Section */}
                <div className="relative py-10 w-full flex flex-col items-center justify-center antialiased">
                    <div className="w-full p-4 flex flex-col justify-center items-center">
                        <h1 className="relative z-10 text-3xl md:text-4xl bg-clip-text text-transparent bg-gradient-to-b from-base_500 to-base_600 text-center font-sans font-bold">
                            Career Opportunities
                        </h1>
                        <p className="text-neutral-500 max-w-lg mx-auto my-2 text-lg text-center relative z-10">
                            Join our team and be part of our mission to make a difference
                        </p>
                    </div>
                    <BackgroundBeams />
                </div>

                {/* Career Listings */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="grid grid-cols-1 gap-8">
                        {careers.map((career) => (
                            <div
                                key={career.id}
                                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 bg-gradient-to-br from-white to-blue-100 border-t-8 border-base_600"
                            >
                                <div className="md:flex">
                                    {/* Image Section */}
                                    <div className="md:w-1/3">
                                        <img
                                            src={career.photo}
                                            alt={career.title}
                                            className="w-full h-48 md:h-full object-cover"
                                        />
                                    </div>

                                    {/* Content Section */}
                                    <div className="p-6 md:w-2/3">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                                    {career.title}
                                                </h2>
                                                <p className="text-lg text-base_600 font-semibold mb-4">
                                                    {career.company_name}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-sm text-gray-500">
                                                    Deadline: {new Date(career.deadline).toLocaleDateString()}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="mb-4">
                                            <p className="text-gray-600 mb-2">
                                                <span className="font-semibold">Location:</span> {career.location}
                                            </p>
                                        </div>

                                        <div
                                            className="text-gray-700 mb-6 line-clamp-3"
                                            dangerouslySetInnerHTML={{ __html: career.description }}
                                        />

                                        <div className="flex flex-wrap gap-4">
                                            <a
                                                href={career.pdf}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-base_600 hover:bg-base_700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-base_500"
                                            >
                                                Download Job Description
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
};

export default Career;